import { Component, HostListener, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { AgencyAddressModel } from 'src/app/account/agency-address';
import { AgencyBranding } from 'src/app/agency-branding';
import { AgencyAddressSharingService } from '../agency-address-sharing.service';
import { ProposalStatusService } from '../proposal-status.service';
import { TopNavigationModel, TopNavigationPermissions } from '../top-navigation-model';
import { TopNavigationService } from '../top-navigation.service';

@Component({
  selector: 'app-top-navigation',
  templateUrl: './top-navigation.component.html',
  styleUrls: ['./top-navigation.component.scss'],
  imports: [
    RouterModule,
  ],
})
export class TopNavigationComponent implements OnInit, OnDestroy {
  readonly #topNavigationService = inject(TopNavigationService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #agencyAddressSharingService = inject(AgencyAddressSharingService);
  readonly #proposalStatusService = inject(ProposalStatusService);

  protected agencyBranding = input<AgencyBranding>();
  protected agencyAddress = output<AgencyAddressModel>();

  topNavigationDetails: TopNavigationModel;
  hostName: string;
  applicantId: number;
  showSwitchAccountsLink: boolean;
  myAccountLink: string;
  switchAccountsLink: string;
  logOutLink: string;
  homePageLink: string;
  documentLink: string;
  policyLink: string;
  certificateLink: string;
  paymentLink: string;
  claimsLink: string;
  hasIntegration: boolean;
  quotesLink: string;
  showProfileOptions: boolean;
  #setPolicyStatus: boolean;
  #setCertificateStatus: boolean;
  #setPaymentConnectorStatus: boolean;
  #setClaimStatus: boolean;
  #setProposalStatus: boolean;
  topNavigationPermissions: TopNavigationPermissions = {
    canDisplayPolicies: false,
    canDisplayCertificates: false,
    canDisplayPayments: false,
    hasClaims: false,
    canDisplayProposals: false,
    isNewProposal: false,
    canDisplayPolicyIndicator: false,
    isCommercialApplicant: false,
  };
  #proposalIndicatorSub: Subscription;
  isPreview: boolean;
  #setSharedPolicySummaryStatus: boolean;

  constructor() {
    this.hostName = window.location.pathname.split('/')[3];
    this.applicantId = +this.#activatedRoute.snapshot.paramMap.get('applicantId');
    this.isPreview = this.#activatedRoute.snapshot.queryParamMap.get('isPreview') === 'true';
  }

  async ngOnInit(): Promise<void> {
    this.showSwitchAccountsLink = false;
    this.#setTopNavigationPermissions();
    await this.#getTopNavigationDetails();
    this.#proposalIndicatorSub = this.#proposalStatusService.isClearNewProposalIndicator.subscribe(async (clearNewProposalIndicator) => {
      if (clearNewProposalIndicator) {
        this.topNavigationPermissions.isNewProposal = !clearNewProposalIndicator;
        await this.#getTopNavigationDetails();
        this.#proposalStatusService.changeNewProposalIndicator(false);
      }
    });
    this.#topNavigationService.setTopNavigationPermissions(this.topNavigationPermissions);
  }

  @HostListener('window:click') click(): void {
    this.hideProfileOptions();
  }

  displayProfileOptions(event: Event): void {
    event.stopPropagation();
    this.showProfileOptions = !this.showProfileOptions;
  }

  hideProfileOptions(): void {
    this.showProfileOptions = false;
  }

  ngOnDestroy(): void {
    this.#proposalIndicatorSub.unsubscribe();
  }

  async #getTopNavigationDetails(): Promise<void> {
    this.topNavigationDetails = await this.#topNavigationService.getTopNavigationDetails(this.hostName, this.applicantId, this.#setPolicyStatus,
      this.#setCertificateStatus, this.#setPaymentConnectorStatus, this.#setClaimStatus, this.#setProposalStatus, this.#setSharedPolicySummaryStatus);

    if (this.topNavigationDetails) {
      this.agencyAddress.emit(this.#getAgencyAddress());
      this.#agencyAddressSharingService.assignAgencyAddress(this.#getAgencyAddress());
      this.#setTopNavigationOptions();
    }
  }

  #getAgencyAddress(): AgencyAddressModel {
    const agencyAddress: AgencyAddressModel = {
      addressLine1: this.topNavigationDetails.addressLine1,
      addressLine2: this.topNavigationDetails.addressLine2,
      agencyName: this.topNavigationDetails.agencyName,
      city: this.topNavigationDetails.city,
      formattedPhoneNumber: this.topNavigationDetails.formattedPhoneNumber,
      phone: this.topNavigationDetails.phone,
      state: this.topNavigationDetails.state,
      zip: this.topNavigationDetails.zip,
    };

    return agencyAddress;
  }

  #setTopNavigationPermissions(): void {
    if (this.applicantId && JSON.parse(window.sessionStorage.getItem('topNavigationPermissions'))) {
      this.topNavigationPermissions = JSON.parse(window.sessionStorage.getItem('topNavigationPermissions'));
      this.#setPolicyStatus = !this.topNavigationPermissions.canDisplayPolicies;
      this.#setCertificateStatus = !this.topNavigationPermissions.canDisplayCertificates;
      this.#setPaymentConnectorStatus = !this.topNavigationPermissions.canDisplayPayments;
      this.#setClaimStatus = !this.topNavigationPermissions.hasClaims;
      this.#setProposalStatus = !this.topNavigationPermissions.canDisplayProposals || !this.topNavigationPermissions.isNewProposal;
      this.#setSharedPolicySummaryStatus = !this.topNavigationPermissions.canDisplayPolicyIndicator;
    } else {
      this.#setPolicyStatus = this.applicantId > 0;
      this.#setCertificateStatus = this.applicantId > 0;
      this.#setPaymentConnectorStatus = this.applicantId > 0;
      this.#setClaimStatus = this.applicantId > 0;
      this.#setProposalStatus = this.applicantId > 0;
      this.#setSharedPolicySummaryStatus = !!this.applicantId;
      window.sessionStorage.removeItem('topNavigationPermissions');
    }
  }

  async #setTopNavigationOptions(): Promise<void> {
    const isMultipleAccountPage = window.location.href.indexOf('/multiple-accounts') > -1;
    this.showSwitchAccountsLink = !this.topNavigationDetails.isAgentView && !isMultipleAccountPage &&
      this.topNavigationDetails.showSwitchAccountsLink;
    if (this.applicantId) {
      this.#setTopNavigationPermissionStatuses();
      window.sessionStorage.setItem('topNavigationPermissions', JSON.stringify(this.topNavigationPermissions));
    }
    this.myAccountLink = this.topNavigationDetails.isAuthenticated ?
      `/EzLynxCustomerService/${this.hostName}/MyAccount/MyAccount/Index/${this.applicantId}` : '';
    this.switchAccountsLink = this.topNavigationDetails.isAuthenticated ?
      `/EzLynxCustomerService/web/${this.hostName}/account/multiple-accounts` : '';
    this.logOutLink = this.topNavigationDetails.isAuthenticated ? `/EzLynxCustomerService/v1/account/${this.hostName}/logoff` : '';
    this.homePageLink = this.applicantId ? `/EzLynxCustomerService/web/${this.hostName}/${this.applicantId}/home` : '';
    this.documentLink = this.applicantId ? `/EzLynxCustomerService/${this.hostName}/DocumentCenter/DocumentCenter/Index/${this.applicantId}` : '';
    this.policyLink = `/EzLynxCustomerService/${this.hostName}/MyPolicies/MyPolicies/Index/${this.applicantId}`;
    this.certificateLink = `/EzLynxCustomerService/${this.hostName}/DocumentCenter/Certificates/Index/${this.applicantId}`;
    this.paymentLink = `/EzLynxCustomerService/${this.hostName}/Payment/Payment/Home/${this.applicantId}`;
    this.claimsLink = `/EzLynxCustomerService/${this.hostName}/Claims/Claims/Index/${this.applicantId}`;

    await this.#getIntegrationStatus();
  }

  #setTopNavigationPermissionStatuses(): void {
    if (this.#setPolicyStatus) {
      this.topNavigationPermissions.canDisplayPolicies = this.topNavigationDetails.canDisplayPolicies;
    }

    if (this.#setCertificateStatus) {
      this.topNavigationPermissions.canDisplayCertificates = this.topNavigationDetails.canDisplayCertificates;
    }

    if (this.#setPaymentConnectorStatus) {
      this.topNavigationPermissions.canDisplayPayments = this.topNavigationDetails.canDisplayPayments;
    }

    if (this.#setClaimStatus) {
      this.topNavigationPermissions.hasClaims = this.topNavigationDetails.canDisplayClaims;
    }

    if (this.#setProposalStatus) {
      this.topNavigationPermissions.canDisplayProposals = this.topNavigationDetails.quoteProposalStatus.isProposalShared;
      this.topNavigationPermissions.isNewProposal = this.topNavigationDetails.quoteProposalStatus.isNewProposal;
    }

    if (this.#setSharedPolicySummaryStatus) {
      this.topNavigationPermissions.canDisplayPolicyIndicator = this.topNavigationDetails.canDisplayPolicyIndicator;
    }
    this.topNavigationPermissions.isCommercialApplicant = this.topNavigationDetails.isCommercial;
  }

  async #getIntegrationStatus(): Promise<void> {
    if (this.applicantId) {
      if (window.sessionStorage.getItem('hasIntegrations') === null) {
        const canDisplayIntegrations = await this.#topNavigationService.canDisplayIntegrations(this.hostName, this.applicantId);
        window.sessionStorage.setItem('hasIntegrations', canDisplayIntegrations ? 'True' : 'False');
        this.hasIntegration = canDisplayIntegrations;
        this.quotesLink = this.hasIntegration ? `/EzLynxCustomerService/${this.hostName}/Integration/IntegrationHome/Home/${this.applicantId}` : '';
        if (this.hasIntegration) {
          this.#topNavigationService.setShowIntegrations(true);
        }
      } else {
        this.hasIntegration = window.sessionStorage.getItem('hasIntegrations') === 'True';
        this.quotesLink = this.hasIntegration ? `/EzLynxCustomerService/${this.hostName}/Integration/IntegrationHome/Home/${this.applicantId}` : '';
        if (this.hasIntegration) {
          this.#topNavigationService.setShowIntegrations(true);
        }
      }
    } else {
      window.sessionStorage.removeItem('hasIntegrations');
    }
  }
}
